import React from 'react';

import {
  Text,
  TextPriority,
  Image,
  ImageResizeOptions,
  Tooltip,
  ThreeDotsLoader,
  IconButton,
  IconButtonThemes,
  ButtonSize,
} from 'wix-ui-tpa/cssVars';

import { Download, Info as InfoIcon } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';

import {
  downloadFile,
  downloadImage,
  getIconByUrl,
  mapFileNameToMediaType,
  formatFileSize,
} from '@/utils/fileUploadUtils';

import { classes, st } from './FilePayload.st.css';

export const FilePayload: React.FC<{
  title?: string;
  size?: number;
  url?: string;
  mediaType?: 'IMAGE' | 'DOCUMENT';
  error?: string;
}> = ({ title, size, url, mediaType: uploadedFileMediaType, error }) => {
  const { t } = useTranslation();
  if (!url && !title) {
    return null;
  }

  const mediaType = uploadedFileMediaType ?? mapFileNameToMediaType(title);

  if (error) {
    return (
      <div
        className={st(
          mediaType === 'IMAGE'
            ? classes.imageErrorContainer
            : classes.documentErrorContainer,
        )}
      >
        <InfoIcon />
        <Text className={st(classes.errorText)}>
          {' '}
          {t('app.widget.fileDisplayError')}
        </Text>
      </div>
    );
  }

  const DownloadIcon = () =>
    !url ? (
      <ThreeDotsLoader className={st(classes.loader)} />
    ) : (
      <Tooltip content={t('app.widgetFileDownload')} appendTo="scrollParent">
        <Download className={st(classes.downloadIcon)} />
      </Tooltip>
    );

  if (mediaType === 'IMAGE') {
    return (
      <div className={st(classes.imageContainer)}>
        <Image
          src={url}
          width={282}
          height={282}
          aspectRatio="square"
          resize={ImageResizeOptions.cover}
          role="img"
          aria-label={title || t('app.widget.aria.fileImageUploading')}
        />

        <IconButton
          theme={IconButtonThemes.Box}
          size={ButtonSize.large}
          className={st(classes.downloadButtonOnImage)}
          aria-label="app.widget.fileDownloadImage"
          onClick={() =>
            url ? downloadImage(url, title || 'image') : undefined
          }
          icon={<DownloadIcon />}
        />
      </div>
    );
  }
  const Icon = getIconByUrl(url ?? title ?? '');

  return (
    <div className={st(classes.container)}>
      <div className={st(classes.iconWrapper)}>
        {Icon && <Icon className={st(classes.fileTypeIcon)} />}
      </div>

      <div className={st(classes.info)}>
        <Text className={st(classes.title)}>{title}</Text>
        <Text className={st(classes.size)} priority={TextPriority.secondary}>
          {formatFileSize(size ?? 0)}
        </Text>
      </div>

      <IconButton
        className={st(classes.downloadButton)}
        aria-label={t('app.widget.aria.fileDownload')}
        onClick={() => (url ? downloadFile(url, title) : undefined)}
        icon={<DownloadIcon />}
      />
    </div>
  );
};
